import React from 'react';
import { cn } from '@/utils/utils';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbSeparator,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
} from './ui/breadcrumb';
import { Link } from 'react-router-dom';
import { useMakeFilteredUrl } from '@/hooks/useFilter';

export type BreadcrumbType = {
  title: React.ReactNode;
  href?: string;
};

type BreadcrumbsContextType = {
  breadcrumbs: BreadcrumbType[] | undefined;
  pushBreadcrumbs: (
    crumbs: React.SetStateAction<BreadcrumbType[] | undefined>
  ) => void;
  popBreadcrumbs: () => void;
  setDefaultBreadcrumbs: (crumbs: BreadcrumbType[] | undefined) => void;
};

const BreadcrumbsContext = React.createContext<BreadcrumbsContextType | null>(
  null
);

export const useBreadcrumbs = () => {
  const context = React.useContext(BreadcrumbsContext);
  if (!context) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbsProvider');
  }
  return context;
};

export const BreadcrumbsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // The stack: [default, ...overrides]
  const [stack, setStack] = React.useState<(BreadcrumbType[] | undefined)[]>([
    undefined,
  ]);

  const breadcrumbs = stack[stack.length - 1];

  const pushBreadcrumbs = (
    crumbs: React.SetStateAction<BreadcrumbType[] | undefined>
  ) => {
    setStack((prev) => [
      ...prev,
      typeof crumbs === 'function' ? crumbs(prev[prev.length - 1]) : crumbs,
    ]);
  };

  const popBreadcrumbs = () => {
    setStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  };

  const setDefaultBreadcrumbs = React.useCallback(
    (crumbs: BreadcrumbType[] | undefined) => {
      setStack((prev) => {
        // Only update if the default actually changed
        const currentDefault = prev[0];
        const isEqual =
          JSON.stringify(currentDefault) === JSON.stringify(crumbs);
        if (isEqual) return prev;
        return [crumbs, ...prev.slice(1)];
      });
    },
    []
  );

  return (
    <BreadcrumbsContext.Provider
      value={{
        breadcrumbs,
        pushBreadcrumbs,
        popBreadcrumbs,
        setDefaultBreadcrumbs,
      }}
    >
      {children}
    </BreadcrumbsContext.Provider>
  );
};

export const ConnectedBreadcrumbs: React.FC<{
  breadcrumbs?: BreadcrumbType[];
  className?: string;
}> = ({ breadcrumbs }) => {
  const make = useMakeFilteredUrl();
  const { breadcrumbs: contextBreadcrumbs, setDefaultBreadcrumbs } =
    useBreadcrumbs();

  // Only set default breadcrumbs if they actually changed
  React.useEffect(() => {
    setDefaultBreadcrumbs(breadcrumbs);
  }, [breadcrumbs, setDefaultBreadcrumbs]);

  const crumbs = contextBreadcrumbs ?? breadcrumbs;

  if (!crumbs?.length) {
    return null;
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {crumbs.map((crumb, index) => (
          <React.Fragment key={index}>
            {!!index && <BreadcrumbSeparator className="hidden md:block" />}
            {index < crumbs.length - 1 ? (
              <BreadcrumbItem key={index} className={cn('hidden md:block')}>
                <BreadcrumbLink asChild>
                  {crumb.href ? (
                    <Link to={make(crumb.href)}>{crumb.title}</Link>
                  ) : (
                    <div>{crumb.title}</div>
                  )}
                </BreadcrumbLink>
              </BreadcrumbItem>
            ) : (
              <BreadcrumbItem key={index}>
                <BreadcrumbPage>{crumb.title}</BreadcrumbPage>
              </BreadcrumbItem>
            )}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export const SetBreadcrumbs = ({
  breadcrumbs,
}: {
  breadcrumbs: React.SetStateAction<BreadcrumbType[] | undefined>;
}) => {
  const { pushBreadcrumbs, popBreadcrumbs } = useBreadcrumbs();

  React.useEffect(() => {
    pushBreadcrumbs(breadcrumbs);
    return () => {
      popBreadcrumbs();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breadcrumbs]);

  return null;
};
